import { Fragment, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import { Download, Search, X } from 'react-bootstrap-icons';
import { CopyButton } from '@/components/ui/CopyButton';
import { ToolPage } from '@/components/ui/ToolPage';
import { clientService } from '@/services/clientService';
import { FileExportService } from '@/utils/fileExport';
import type { Client, ClientConfig } from '@/types';
import type { ToolResultFile } from '@/types/tools';
import './ConfigSearchTool.css';

interface MatchDetail {
  label: string;
  value?: string;
  status: 'matched' | 'missing';
}

interface SearchResult {
  clientName: string;
  matchedFields: string[];
  matchDetails: MatchDetail[];
  config: ClientConfig;
  isDev: boolean;
  accountId?: string;
}

// Predefined quick filters
// matchType: 'equals' (default) - exact match, 'exists' - field is defined and truthy
const QUICK_FILTERS = [
  { label: 'Agents on', field: 'agents', value: true },
  { label: 'Integrations on', field: 'pipedreamIntegrations', value: true },
  { label: 'All production apps', field: 'allProdApps', value: true },
  { label: 'Dev stacks', field: 'devInstance', value: true },
  { label: 'Q Business resources', field: 'provisionQResources', value: true },
  { label: 'Numa Files on Bedrock', field: 'preferredKnowledgeBase', value: 'bedrock' },
  { label: 'Shares its Bedrock quota', field: 'allowBedrockQuotaSharing', value: true },
  { label: "Uses another account's Bedrock quota", field: 'bedrockAccount', value: true, matchType: 'exists' as const },
];

export default function ConfigSearchTool() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [expandedClient, setExpandedClient] = useState<string | null>(null);

  // Load clients on mount
  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const allClients = await clientService.getAllClients();
      setClients(allClients);
    } catch (err) {
      console.error('Failed to load clients:', err);
      setError(
        'Could not load the client configs. Try again, and if it keeps failing, check that your sign-in has access.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Search and filter logic
  const searchResults = useMemo((): SearchResult[] => {
    const findAppMatches = (config: ClientConfig, value: string) => {
      const apps = (config.apps || {}) as Record<string, unknown>;
      const target = value.toLowerCase();
      const matches = Object.keys(apps).filter((appName) => appName.toLowerCase().includes(target));
      const allAppsEnabled = config.allApps === true || config.allProdApps === true;
      return { matches, allAppsEnabled };
    };

    const stringifyLower = (value: unknown): string => {
      try {
        return JSON.stringify(value).toLowerCase();
      } catch {
        return '';
      }
    };

    const formatValue = (value: unknown): string => {
      if (value === undefined || value === null) return '';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
    };

    const unquote = (value: string): string => {
      const trimmed = value.trim();
      if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
        return trimmed.slice(1, -1);
      }
      return trimmed;
    };

    const splitFieldQuery = (expr: string): { field: string; value: string; op: '=' | '==' } | null => {
      const idxDouble = expr.indexOf('==');
      if (idxDouble !== -1) {
        const field = expr.slice(0, idxDouble).trim();
        const value = expr.slice(idxDouble + 2).trim();
        if (!field) return null;
        return { field, value, op: '==' };
      }

      const idx = expr.indexOf('=');
      if (idx === -1) return null;
      const field = expr.slice(0, idx).trim();
      const value = expr.slice(idx + 1).trim();
      if (!field) return null;
      return { field, value, op: '=' };
    };

    const rawQuery = searchQuery.trim();
    const tokens = rawQuery
      .split(/[\n,]+/)
      .map((t) => t.trim())
      .filter(Boolean);

    const results: SearchResult[] = [];

    for (const client of clients) {
      const configKeyByLower = new Map<string, string>(
        Object.keys(client.config || {}).map((k) => [k.toLowerCase(), k])
      );
      const matchedFields = new Set<string>();
      const matchDetails: MatchDetail[] = [];
      let matches = true;

      // Apply quick filters (AND logic)
      if (activeFilters.size > 0) {
        for (const filterLabel of activeFilters) {
          const filter = QUICK_FILTERS.find((f) => f.label === filterLabel);
          if (filter) {
            const configValue = client.config[filter.field as keyof ClientConfig];
            const matchType = 'matchType' in filter ? filter.matchType : 'equals';

            let filterMatched = false;
            if (matchType === 'exists') {
              // Check if field exists and is truthy
              filterMatched = configValue !== undefined && configValue !== null && configValue !== '';
            } else {
              // Default: exact value match
              filterMatched = configValue === filter.value;
            }

            if (!filterMatched) {
              matches = false;
              break;
            } else {
              matchedFields.add(filter.field);
              matchDetails.push({
                label: filter.label,
                value: formatValue(configValue),
                status: 'matched',
              });
            }
          }
        }
      }

      if (!matches) continue;

      // Apply text search
      if (tokens.length > 0) {
        for (const token of tokens) {
          const isNegative = token.startsWith('!');
          const base = isNegative ? token.slice(1).trim() : token;
          if (!base) continue;
          const query = base.trim();

          const fieldQuery = splitFieldQuery(query);
          if (fieldQuery) {
            const rawField = fieldQuery.field;
            const rawValue = unquote(fieldQuery.value);
            const fieldLower = rawField.toLowerCase();
            const valueLower = rawValue.toLowerCase();
            const resolvedField = configKeyByLower.get(fieldLower) || rawField;
            const configValue = client.config[resolvedField as keyof ClientConfig];
            const isExact = fieldQuery.op === '==';

            let fieldMatched = false;
            if (fieldLower === 'apps' || fieldLower === 'app') {
              const { matches: appMatches, allAppsEnabled } = findAppMatches(client.config, rawValue);
              fieldMatched = appMatches.length > 0 || allAppsEnabled;
              if (!isNegative && fieldMatched) {
                matchedFields.add(`apps=${rawValue}`);
                matchDetails.push({
                  label: `An app named like "${rawValue}"`,
                  value: [allAppsEnabled ? 'All apps on' : null, ...appMatches].filter(Boolean).join(', '),
                  status: 'matched',
                });
              }
            } else if (fieldLower === 'client' || fieldLower === 'name') {
              fieldMatched = client.name.toLowerCase().includes(valueLower);
              if (!isNegative && fieldMatched) {
                matchedFields.add(`client=${rawValue}`);
                matchDetails.push({
                  label: `Client name holds "${rawValue}"`,
                  value: client.name,
                  status: 'matched',
                });
              }
            } else {
              if (configValue !== undefined) {
                if (typeof configValue === 'boolean') {
                  fieldMatched =
                    (valueLower === 'true' && configValue === true) ||
                    (valueLower === 'false' && configValue === false);
                } else if (typeof configValue === 'number') {
                  const parsed = Number(rawValue);
                  fieldMatched = !Number.isNaN(parsed) && configValue === parsed;
                } else if (typeof configValue === 'string') {
                  const candidate = configValue.toLowerCase();
                  fieldMatched = isExact ? candidate === valueLower : candidate.includes(valueLower);
                } else {
                  fieldMatched = stringifyLower(configValue).includes(valueLower);
                }
              }
              if (!isNegative && fieldMatched) {
                matchedFields.add(`${resolvedField}${fieldQuery.op}${rawValue}`);
                matchDetails.push({
                  label: `${resolvedField}${fieldQuery.op}${rawValue}`,
                  value: formatValue(configValue),
                  status: 'matched',
                });
              }
            }

            if (isNegative) {
              if (fieldMatched) {
                matches = false;
                break;
              } else {
                matchedFields.add(`!${resolvedField}${fieldQuery.op}${rawValue}`);
                matchDetails.push({
                  label: `${resolvedField}${fieldQuery.op}${rawValue}`,
                  value: 'not in this config',
                  status: 'missing',
                });
              }
            } else if (!fieldMatched) {
              matches = false;
              break;
            }
          } else {
            const queryLower = query.toLowerCase();

            // Free text search - search entire config JSON (keys + values), plus client name
            const clientNameMatches = client.name.toLowerCase().includes(queryLower);
            const configJsonStr = stringifyLower(client.config);
            const textMatched = configJsonStr.includes(queryLower) || clientNameMatches;

            if (isNegative) {
              if (textMatched) {
                matches = false;
                break;
              } else {
                matchedFields.add(`!${base}`);
                matchDetails.push({
                  label: `Holds "${base}"`,
                  value: 'not in this config',
                  status: 'missing',
                });
              }
            } else {
              if (textMatched) {
                matchedFields.add(base);
                matchDetails.push({
                  label: `Holds "${base}"`,
                  value: clientNameMatches ? client.name : base,
                  status: 'matched',
                });
              } else {
                matches = false;
                break;
              }
            }
          }
        }
      }

      if (matches) {
        results.push({
          clientName: client.name,
          matchedFields: [...new Set(matchedFields)],
          matchDetails,
          config: client.config,
          isDev: client.config.devInstance === true,
          accountId: client.config.clientAccountId,
        });
      }
    }

    // Sort by client name
    return results.sort((a, b) => a.clientName.localeCompare(b.clientName));
  }, [clients, searchQuery, activeFilters]);

  const toggleFilter = (filterLabel: string) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(filterLabel)) {
        next.delete(filterLabel);
      } else {
        next.add(filterLabel);
      }
      return next;
    });
  };

  const clearFilters = () => {
    setActiveFilters(new Set());
    setSearchQuery('');
  };

  const handleExportCSV = () => {
    if (searchResults.length === 0) return;

    const headers = [
      'Client Name',
      'Is Dev',
      'Region',
      'Account ID',
      'Matched Fields',
      'Apps',
      'Agents',
      'Integrations',
      'All Prod Apps',
      'Numa Files backend',
    ];
    const rows = searchResults.map((r) => [
      r.clientName,
      r.isDev ? 'Yes' : 'No',
      r.config.region || '',
      r.accountId || '',
      (r.matchDetails.length > 0 ? r.matchDetails : r.matchedFields.map((label) => ({ label }) as MatchDetail))
        .map((md) => md.label)
        .join('; '),
      Object.keys(r.config.apps || {}).join('; '),
      r.config.agents ? 'Yes' : 'No',
      r.config.pipedreamIntegrations ? 'Yes' : 'No',
      r.config.allProdApps ? 'Yes' : 'No',
      r.config.preferredKnowledgeBase || 'bedrock',
    ]);

    const csv = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => (String(cell).includes(',') ? `"${cell}"` : cell)).join(',')),
    ].join('\n');

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').substring(0, 19);
    const file: ToolResultFile = {
      name: `config-search-results-${timestamp}.csv`,
      content: csv,
      mimeType: 'text/csv',
      size: new Blob([csv]).size,
    };

    FileExportService.downloadFile(file);
  };

  // With no search and no filter the page lists every client.
  const filtering = Boolean(searchQuery.trim() || activeFilters.size > 0);
  const clientsWord = (n: number) => (n === 1 ? '1 client' : `${n} clients`);

  return (
    <ToolPage
      title="Config search"
      subtitle="Find clients by keyword, or by the features they have switched on."
      actions={
        <Button
          variant="outline-secondary"
          onClick={handleExportCSV}
          disabled={searchResults.length === 0}
          title="Download the clients listed below. Bulk update client config can read this file."
        >
          <Download className="me-1" aria-hidden="true" />
          Export CSV
        </Button>
      }
    >
      <Card className="mb-3">
        <Card.Body>
          <InputGroup>
            <InputGroup.Text>
              <Search aria-hidden="true" />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="agents=true, region==us-east-1, or any text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search client configs"
              autoFocus
            />
            {searchQuery && (
              <Button variant="outline-secondary" onClick={() => setSearchQuery('')} aria-label="Clear the search">
                <X aria-hidden="true" />
              </Button>
            )}
          </InputGroup>

          <div className="config-search__filters">
            <span className="config-search__filters-label">Quick filters</span>
            {QUICK_FILTERS.map((filter) => {
              const active = activeFilters.has(filter.label);
              return (
                <button
                  key={filter.label}
                  type="button"
                  className={`config-search__chip${active ? ' config-search__chip--active' : ''}`}
                  aria-pressed={active}
                  onClick={() => toggleFilter(filter.label)}
                >
                  {filter.label}
                  {active && <X size={12} aria-hidden="true" />}
                </button>
              );
            })}
          </div>

          <details className="config-search__help">
            <summary>How to write a search</summary>
            <ul>
              <li>
                <span className="mono">agents=true</span> finds clients where the field holds the value. For text, part
                of the value is enough.
              </li>
              <li>
                <span className="mono">region==us-east-1</span> finds clients where the field is exactly the value.
              </li>
              <li>
                <span className="mono">apps=meeting</span> finds clients with an app named like that.{' '}
                <span className="mono">client=acme</span> looks in the client name.
              </li>
              <li>
                <span className="mono">!agents=true</span> leaves out the clients that match.
              </li>
              <li>Any other text is looked for in the whole config and in the client name.</li>
              <li>Put a comma between terms. A client must match every term and every quick filter.</li>
            </ul>
            <p>Field names ignore capitals.</p>
          </details>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger">
          {error}{' '}
          <Button variant="link" className="p-0 align-baseline" onClick={loadClients}>
            Try again
          </Button>
        </Alert>
      )}

      {!loading && !error && (
        <div className="config-search__summary">
          <span>
            {filtering
              ? `${searchResults.length} of ${clientsWord(clients.length)} match`
              : `All ${clientsWord(clients.length)}`}
          </span>
          {filtering && (
            <Button variant="outline-secondary" size="sm" onClick={clearFilters}>
              Clear the search and filters
            </Button>
          )}
        </div>
      )}

      <Card>
        <div className="config-search__scroll">
          <Table className="mb-0 config-search__table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Region</th>
                <th>AWS account</th>
                <th>Apps</th>
                <th>{filtering ? 'Why it matched' : ''}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={6} className="config-search__empty">
                    <Spinner size="sm" className="me-2" />
                    Loading client configs
                  </td>
                </tr>
              )}

              {!loading && !error && clients.length === 0 && (
                <tr>
                  <td colSpan={6} className="config-search__empty">
                    No client has a config yet.
                  </td>
                </tr>
              )}

              {!loading && error && (
                <tr>
                  <td colSpan={6} className="config-search__empty">
                    The client configs did not load, so there is nothing to search.
                  </td>
                </tr>
              )}

              {!loading && clients.length > 0 && searchResults.length === 0 && (
                <tr>
                  <td colSpan={6} className="config-search__empty">
                    No client matches that search. Check the field name, or clear a filter.
                  </td>
                </tr>
              )}

              {!loading &&
                searchResults.map((result) => {
                  const isExpanded = expandedClient === result.clientName;
                  const matchDetails =
                    result.matchDetails.length > 0
                      ? result.matchDetails
                      : result.matchedFields.map((label) => ({ label, status: 'matched' as const }) as MatchDetail);
                  // Only the open row needs its config written out.
                  const json = isExpanded ? JSON.stringify(result.config, null, 2) : '';

                  return (
                    <Fragment key={result.clientName}>
                      <tr className={isExpanded ? 'config-search__row--open' : undefined}>
                        <td>
                          <span className="config-search__name">{result.clientName}</span>
                          {result.isDev && (
                            <Badge bg="warning" className="ms-2">
                              Dev
                            </Badge>
                          )}
                        </td>
                        <td className="mono">{result.config.region || 'not set'}</td>
                        <td className="mono">
                          {result.accountId ? (
                            <>
                              {result.accountId}
                              <CopyButton value={result.accountId} label="Copy account ID" />
                            </>
                          ) : (
                            'not set'
                          )}
                        </td>
                        <td>
                          {result.config.allApps
                            ? 'All apps'
                            : result.config.allProdApps
                              ? 'All production apps'
                              : `${Object.keys(result.config.apps || {}).length} chosen`}
                        </td>
                        <td>
                          {matchDetails.map((detail, idx) => (
                            <div key={idx} className="config-search__match">
                              <Badge bg={detail.status === 'missing' ? 'secondary' : 'success'}>
                                {detail.status === 'missing' ? 'Does not have' : 'Matched'}
                              </Badge>
                              <span className="config-search__match-label">{detail.label}</span>
                              {detail.value && (
                                <span className="config-search__match-value" title={detail.value}>
                                  {detail.value}
                                </span>
                              )}
                            </div>
                          ))}
                        </td>
                        <td className="config-search__actions">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setExpandedClient(isExpanded ? null : result.clientName)}
                            aria-expanded={isExpanded}
                          >
                            {isExpanded ? 'Hide config' : 'Show config'}
                          </Button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="config-search__detail">
                          <td colSpan={6}>
                            <div className="config-search__detail-grid">
                              <div>
                                <h3 className="section-label">Client</h3>
                                <dl className="tool-facts">
                                  <dt>Name</dt>
                                  <dd>{result.clientName}</dd>
                                  <dt>AWS account</dt>
                                  <dd className="mono">{result.accountId || 'not set'}</dd>
                                  <dt>Region</dt>
                                  <dd className="mono">{result.config.region || 'not set'}</dd>
                                  <dt>Custom domain</dt>
                                  <dd className="mono">{result.config.customDomain || 'not set'}</dd>
                                  <dt>Numa Files backend</dt>
                                  <dd>{result.config.preferredKnowledgeBase || 'bedrock'}</dd>
                                  <dt>Integrations</dt>
                                  <dd>{result.config.pipedreamIntegrations ? 'On' : 'Off'}</dd>
                                  <dt>Agents</dt>
                                  <dd>{result.config.agents ? 'On' : 'Off'}</dd>
                                  <dt>All production apps</dt>
                                  <dd>{result.config.allProdApps ? 'Yes' : 'No'}</dd>
                                </dl>
                                <Link
                                  className="btn btn-outline-secondary btn-sm mt-3"
                                  to={`/tools/update-client-config?clientName=${encodeURIComponent(result.clientName)}`}
                                >
                                  Update this config
                                </Link>
                              </div>
                              <div className="config-search__json-block">
                                <div className="config-search__json-head">
                                  <h3 className="section-label">Config</h3>
                                  <CopyButton value={json} label="Copy config JSON" />
                                </div>
                                <pre className="tool-json config-search__json">{json}</pre>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
            </tbody>
          </Table>
        </div>
      </Card>
    </ToolPage>
  );
}
